import { Helmet } from 'react-helmet-async';

interface BreadcrumbItem {
  name: string;
  url: string;
}

interface ReviewData {
  ratingValue: number;
  reviewCount: number;
  bestRating?: number;
  worstRating?: number;
}

interface FAQItem {
  question: string;
  answer: string;
}

interface SEOHeadProps {
  title: string;
  description: string;
  /** Overrides the Open Graph / Twitter card title (falls back to the page title). */
  ogTitle?: string;
  /** Overrides the Open Graph / Twitter card description (falls back to the meta description). */
  ogDescription?: string;
  canonical?: string;
  type?: 'website' | 'product' | 'article';
  image?: string;
  keywords?: string[];
  productData?: {
    name: string;
    description: string;
    price: string;
    currency: string;
    image: string;
    brand: string;
    sku?: string;
    gtin?: string;
    availability?: 'InStock' | 'OutOfStock' | 'PreOrder';
    category?: string;
  };
  articleData?: {
    publishedTime: string;
    modifiedTime?: string;
    author?: string;
    section?: string;
    tags?: string[];
  };
  breadcrumbs?: BreadcrumbItem[];
  reviewData?: ReviewData;
  faqData?: FAQItem[];
  noindex?: boolean;
}

const BASE_URL = 'https://protonhealthlabs.com';
const DEFAULT_IMAGE = `${BASE_URL}/og/proton-home-social.jpg`;

// Generate dynamic OG image URL
function generateOgImageUrl(title: string, category?: string): string {
  const baseUrl = import.meta.env.VITE_SUPABASE_URL;
  if (!baseUrl) return DEFAULT_IMAGE;
  const params = new URLSearchParams({
    title: title.slice(0, 100),
    category: category || 'Health & Wellness',
  });
  return `${baseUrl}/functions/v1/generate-og-image?${params.toString()}`;
}

export function SEOHead({
  title,
  ogTitle,
  ogDescription,
  description,
  canonical,
  type = 'website',
  image,
  keywords,
  productData,
  articleData,
  breadcrumbs,
  reviewData,
  faqData,
  noindex = false,
}: SEOHeadProps) {
  // Avoid duplicated brand suffixes when a page already includes the brand in its title.
  const fullTitle = title.includes('Proton Health Labs')
    ? title
    : `${title} | Proton Health Labs™`;
  const socialTitle = ogTitle || fullTitle;
  // Canonicals are always origin + pathname only — never query/UTM params.
  const normalizeCanonical = (c?: string): string => {
    try {
      const raw = c || (typeof window !== 'undefined' ? window.location.pathname : '/');
      const parsed = new URL(raw, BASE_URL);
      return `${BASE_URL}${parsed.pathname}`;
    } catch {
      return BASE_URL;
    }
  };
  const url = normalizeCanonical(canonical);
  
  // Use provided image, or generate dynamic OG image for articles, or default
  const ogImage = image || (type === 'article' 
    ? generateOgImageUrl(title, articleData?.section)
    : DEFAULT_IMAGE);

  // Breadcrumb Schema
  const breadcrumbSchema = breadcrumbs && breadcrumbs.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url.startsWith('http') ? item.url : `${BASE_URL}${item.url}`
    }))
  } : null;

  // Product Schema with enhanced data
  const productSchema = productData ? {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": productData.name,
    "description": productData.description,
    "image": productData.image.startsWith('http') ? productData.image : `${BASE_URL}${productData.image}`,
    "brand": {
      "@type": "Brand",
      "name": productData.brand
    },
    "sku": productData.sku,
    "gtin": productData.gtin,
    "category": productData.category || "Dietary Supplements",
    ...(reviewData && {
      "aggregateRating": {
        "@type": "AggregateRating",
        "ratingValue": reviewData.ratingValue,
        "reviewCount": reviewData.reviewCount,
        "bestRating": reviewData.bestRating || 5,
        "worstRating": reviewData.worstRating || 1
      }
    }),
    "offers": {
      "@type": "Offer",
      "url": url,
      "priceCurrency": productData.currency,
      "price": productData.price,
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      ...(productData.availability ? { "availability": `https://schema.org/${productData.availability}` } : {}),
      "seller": {
        "@type": "Organization",
        "name": "Proton Health Labs"
      }
    }
  } : null;

  // FAQ Schema
  const faqSchema = faqData && faqData.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqData.map((item) => ({
      "@type": "Question",
      "name": item.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": item.answer
      }
    }))
  } : null;

  // Article Schema
  const articleSchema = type === 'article' && articleData ? {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": title,
    "description": description,
    "image": ogImage,
    "datePublished": articleData.publishedTime,
    "dateModified": articleData.modifiedTime || articleData.publishedTime,
    "author": {
      "@type": "Organization",
      "name": articleData.author || "Proton Health Labs",
      "url": BASE_URL
    },
    "publisher": {
      "@type": "Organization",
      "name": "Proton Health Labs",
      "logo": {
        "@type": "ImageObject",
        "url": DEFAULT_IMAGE
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": url
    },
    "articleSection": articleData.section,
    "keywords": articleData.tags?.join(", ")
  } : null;

  // Organization Schema
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Proton Health Labs",
    "slogan": "Be a Proton™ — Always Positive",
    "url": BASE_URL,
    "logo": DEFAULT_IMAGE,
    "sameAs": [
      "https://www.instagram.com/protonhealthlabs",
      "https://www.facebook.com/protonhealthlabs",
      "https://www.youtube.com/@protonhealthlabs"
    ],
    "contactPoint": {
      "@type": "ContactPoint",
      "telephone": "+1-732-513-8942",
      "contactType": "customer service",
      "availableLanguage": "English"
    }
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      
      {/* Keywords */}
      {keywords && keywords.length > 0 && (
        <meta name="keywords" content={keywords.join(', ')} />
      )}
      
      {/* Robots */}
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type === 'product' ? 'product' : type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={socialTitle} />
      <meta property="og:description" content={ogDescription || description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={title} />
      <meta property="og:site_name" content="Proton Health Labs" />
      <meta property="og:locale" content="en_US" />

      {/* Product-specific Open Graph tags */}
      {productData && (
        <>
          <meta property="product:price:amount" content={productData.price} />
          <meta property="product:price:currency" content={productData.currency} />
          {productData.availability && (
            <meta property="product:availability" content={productData.availability === 'InStock' ? 'in stock' : productData.availability === 'OutOfStock' ? 'out of stock' : 'preorder'} />
          )}
          <meta property="product:brand" content={productData.brand} />
        </>
      )}

      {/* Article-specific Open Graph tags */}
      {type === 'article' && articleData && (
        <>
          <meta property="article:published_time" content={articleData.publishedTime} />
          {articleData.modifiedTime && (
            <meta property="article:modified_time" content={articleData.modifiedTime} />
          )}
          {articleData.author && (
            <meta property="article:author" content={articleData.author} />
          )}
          {articleData.section && (
            <meta property="article:section" content={articleData.section} />
          )}
          {articleData.tags?.map((tag, index) => (
            <meta key={index} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={socialTitle} />
      <meta name="twitter:description" content={ogDescription || description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={title} />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      
      {breadcrumbSchema && (
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      )}
      
      {productSchema && (
        <script type="application/ld+json">
          {JSON.stringify(productSchema)}
        </script>
      )}
      
      {faqSchema && (
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      )}
      
      {articleSchema && (
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      )}
    </Helmet>
  );
}
